/**
 * The `report_issue` work-agent tool — the FE half of its wire contract.
 *
 * The tool is approval-gated on the backend with its own approval kind, so a
 * pending call renders as a report card (`ReportIssueApprovalBlock`) and a
 * resolved call renders the result card via the `report-issue` tool card kind.
 * A bug report carries the model's drafted `title` / `description` for the
 * user to edit. Every diagnostic field is assembled server-side. The tool no
 * longer files clinical safety reports; `safety` survives in the types only
 * for cards parked before that change and receipts already in the timeline.
 */

export const REPORT_ISSUE_TOOL_NAME = 'report_issue';

/** Mirrors ml-scribe's `APPROVAL_KIND_REPORT_ISSUE`. */
export const REPORT_ISSUE_APPROVAL_KIND = 'report_issue';

export type ReportIssueType = 'bug' | 'safety';

export interface ReportIssueDraftInput {
  issueType: ReportIssueType;
  title: string;
  description: string;
}

export const isReportIssueToolName = (name: string): boolean =>
  name === REPORT_ISSUE_TOOL_NAME;

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Tolerant read of the tool input the draft card edits. */
export const parseReportIssueDraftInput = (
  toolInput: Record<string, unknown>
): ReportIssueDraftInput => ({
  issueType: toolInput.issue_type === 'safety' ? 'safety' : 'bug',
  title: readString(toolInput.title),
  description: readString(toolInput.description),
});

export interface ReportIssueResultData {
  issueType: ReportIssueType;
  ticketReference: string;
}

/** Tolerant read of the tool result's `data` payload; null when malformed. */
export const parseReportIssueResultData = (
  data: Record<string, unknown> | undefined
): ReportIssueResultData | null => {
  if (!data) return null;
  const ticketReference = readString(data.ticket_reference);
  if (!ticketReference) return null;
  return {
    issueType: data.issue_type === 'safety' ? 'safety' : 'bug',
    ticketReference,
  };
};
